import { SfxToggle, useSfx } from '@slingu/ui';
import { useEffect, useRef, useState } from 'react';

import { commitScore, getHighScore } from './highscores.js';
import { LEGACY_POOL } from './vocabulary.js';

const HIGH_SCORE_ID = 'word-slots';

export type WordSlotsProps = {
  onExit: (result: { score: number; spins: number; jackpots: number; xp: number }) => void;
};

type Phase = 'menu' | 'playing' | 'over';

type Reel = {
  items: string[];     // pool of candidate words (correct + distractors) to cycle
  offset: number;      // visual offset for spinning animation
  spinning: boolean;
  stopped: string | null; // final word shown when stopped
  stoppedCorrect: boolean;
};

const REELS = 3;
const SPINS_PER_GAME = 10;
const SPIN_DURATION_MS = 1800;
const BASE_SPEED = 220; // px/s item cycle speed per reel (for effect)

// Each reel visually shows items stacked vertically, one is the "window"
const ITEM_H = 64;  // pixel height per reel slot
const ITEM_POOL_SIZE = 12;

/**
 * Word Slots — caça-níqueis educativo. Ao apertar SPIN, 3 reels começam a
 * girar com palavras em inglês. O jogador bate em STOP cada reel no momento
 * certo (quando a tradução correta da palavra-alvo em português passa). Cada
 * reel que parar na palavra correta = XP. 3 de 3 = JACKPOT.
 *
 * Mistura cassino + reflexo + leitura rápida.
 */
export function WordSlots({ onExit }: WordSlotsProps) {
  const sfx = useSfx();
  const sfxRef = useRef(sfx);
  sfxRef.current = sfx;

  const [phase, setPhase] = useState<Phase>('menu');
  const [target, setTarget] = useState<{ en: string; pt: string } | null>(null);
  const [reels, setReels] = useState<Reel[]>([]);
  const [spinning, setSpinning] = useState(false);
  const [spinsLeft, setSpinsLeft] = useState(SPINS_PER_GAME);
  const [score, setScore] = useState(0);
  const [highScore, setHighScore] = useState<number>(() => getHighScore(HIGH_SCORE_ID));
  const [newRecord, setNewRecord] = useState<boolean>(false);
  const [jackpots, setJackpots] = useState(0);
  const [flash, setFlash] = useState<null | 'win' | 'jackpot' | 'miss'>(null);
  const spinStartRef = useRef(0);

  function shuffle<T>(arr: T[]): T[] {
    const a = [...arr];
    for (let i = a.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [a[i], a[j]] = [a[j]!, a[i]!];
    }
    return a;
  }

  function prepRound() {
    const pair = LEGACY_POOL[Math.floor(Math.random() * LEGACY_POOL.length)]!;
    const pool = [
      pair.correct,
      ...shuffle(LEGACY_POOL.filter((p) => p.correct !== pair.correct).map((p) => p.correct)).slice(0, ITEM_POOL_SIZE - 1),
    ];
    setTarget({ en: pair.correct.toUpperCase(), pt: pair.promptPt ?? pair.correct });
    setReels(
      Array.from({ length: REELS }, () => ({
        items: shuffle(pool),
        offset: Math.random() * 400,
        spinning: false,
        stopped: null,
        stoppedCorrect: false,
      })),
    );
    setFlash(null);
  }

  function startGame() {
    setScore(0);
    setJackpots(0);
    setSpinsLeft(SPINS_PER_GAME);
    prepRound();
    setPhase('playing');
    sfxRef.current.play('start');
  }

  function spin() {
    if (spinning || spinsLeft <= 0) return;
    sfxRef.current.play('whoosh');
    setSpinsLeft((s) => s - 1);
    setSpinning(true);
    setFlash(null);
    spinStartRef.current = performance.now();
    setReels((prev) =>
      prev.map((r) => ({
        ...r,
        items: shuffle(r.items),
        offset: 0,
        spinning: true,
        stopped: null,
        stoppedCorrect: false,
      })),
    );
    // Auto-stop all reels in sequence if the player doesn't manually stop
    for (let i = 0; i < REELS; i++) {
      window.setTimeout(() => {
        stopReel(i);
      }, SPIN_DURATION_MS + i * 400);
    }
  }

  function stopReel(index: number) {
    setReels((prev) => {
      const next = [...prev];
      const r = next[index];
      if (!r || !r.spinning) return prev;
      // Determine which item is in window at current offset
      const pos = Math.floor(((r.offset % (r.items.length * ITEM_H)) / ITEM_H) + r.items.length) % r.items.length;
      const word = r.items[pos]!;
      const correct = !!target && word.toUpperCase() === target.en;
      next[index] = { ...r, spinning: false, stopped: word, stoppedCorrect: correct };
      sfxRef.current.play('tap');
      if (correct) sfxRef.current.play('xp-gain');
      return next;
    });
  }

  // Animate spinning reels via rAF
  useEffect(() => {
    if (!spinning) return;
    let raf = 0;
    let last = performance.now();
    function loop(now: number) {
      const dt = (now - last) / 1000;
      last = now;
      setReels((prev) => {
        let anySpinning = false;
        const next = prev.map((r) => {
          if (!r.spinning) return r;
          anySpinning = true;
          return { ...r, offset: r.offset + BASE_SPEED * dt * (1 + Math.random() * 0.15) };
        });
        if (!anySpinning) setSpinning(false);
        return next;
      });
      if (spinning) raf = requestAnimationFrame(loop);
    }
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [spinning]);

  // Resolve score once all reels stop
  useEffect(() => {
    if (reels.length === 0) return;
    if (reels.some((r) => r.spinning)) return;
    if (reels.every((r) => r.stopped === null)) return;
    // Score
    const correct = reels.filter((r) => r.stoppedCorrect).length;
    if (correct === 0) {
      setFlash('miss');
    } else if (correct === REELS) {
      setScore((s) => s + 80);
      setJackpots((j) => j + 1);
      setFlash('jackpot');
      sfxRef.current.play('level-up');
    } else {
      setScore((s) => s + correct * 20);
      setFlash('win');
    }
    // Next round or end
    window.setTimeout(() => {
      if (spinsLeft <= 0) {
        const result = commitScore(HIGH_SCORE_ID, score);
        setHighScore(result.best);
        setNewRecord(result.isNew);
        setPhase('over');
      } else {
        prepRound();
      }
    }, 1600);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reels.map((r) => r.stopped).join('|'), reels.every((r) => r.stopped !== null && !r.spinning)]);

  return (
    <div className="sl-minigame sl-minigame--portrait">
      <button
        type="button"
        className="sl-minigame__close"
        onClick={() => onExit({ score, spins: SPINS_PER_GAME - spinsLeft, jackpots, xp: score })}
        aria-label="Fechar jogo"
      >
        ×
      </button>
      <div className="sl-flappy__sfx"><SfxToggle variant="icon" /></div>

      {phase === 'menu' ? (
        <div className="sl-flappy__menu">
          <h2><span aria-hidden>🎰</span> Word Slots</h2>
          <p>
            Gire os 3 reels e pare cada um quando a tradução correta passar.
            +20 XP por reel certo, <strong>JACKPOT</strong> de 80 XP nos 3/3.
            10 giros por partida, pool de 1000+ palavras.
          </p>
          {highScore > 0 ? (
            <p className="sl-flappy__record">
              🏆 Seu recorde: <strong>{highScore} XP</strong>
            </p>
          ) : null}
          <div className="sl-flappy__menu-actions">
            <button type="button" className="sl-btn sl-btn--primary sl-btn--lg sl-btn--upper"
              onClick={() => { setNewRecord(false); startGame(); }}>Começar</button>
          </div>
        </div>
      ) : null}

      {phase === 'playing' && target ? (
        <div className="sl-wslots">
          <div className="sl-wbuilder__hud">
            <span>Giros {SPINS_PER_GAME - spinsLeft}/{SPINS_PER_GAME}</span>
            <span>🎰 {jackpots}</span>
            <span className="sl-wbuilder__hud-item--xp">{score} XP</span>
          </div>
          <div className="sl-wbuilder__prompt">
            <span className="sl-wbuilder__prompt-eyebrow">PARE NA TRADUÇÃO DE</span>
            <p className="sl-wbuilder__prompt-text">{target.pt}</p>
          </div>

          <div className="sl-wslots__machine">
            {reels.map((r, i) => (
              <div key={i} className="sl-wslots__reel">
                <div className="sl-wslots__window">
                  <div
                    className={`sl-wslots__strip${r.spinning ? ' sl-wslots__strip--spin' : ''}`}
                    style={{ transform: `translateY(-${r.offset % (r.items.length * ITEM_H)}px)` }}
                  >
                    {/* Double the items so wrap is seamless */}
                    {[...r.items, ...r.items].map((item, j) => (
                      <div key={j} className="sl-wslots__item">{item}</div>
                    ))}
                  </div>
                </div>
                <button
                  type="button"
                  className={`sl-wslots__stop${r.stoppedCorrect ? ' sl-wslots__stop--ok' : ''}${r.stopped && !r.stoppedCorrect ? ' sl-wslots__stop--miss' : ''}`}
                  onClick={() => stopReel(i)}
                  disabled={!r.spinning}
                >
                  {r.spinning ? 'STOP' : r.stopped ? (r.stoppedCorrect ? '✓' : '✗') : '–'}
                </button>
              </div>
            ))}
          </div>

          <div className="sl-wslots__actions">
            <button
              type="button"
              className="sl-btn sl-btn--primary sl-btn--lg sl-btn--upper"
              onClick={spin}
              disabled={spinning || spinsLeft <= 0}
            >{spinning ? 'Girando…' : 'GIRAR'}</button>
          </div>

          {flash ? (
            <div className={`sl-wslots__flash sl-wslots__flash--${flash}`}>
              {flash === 'jackpot' ? 'JACKPOT! 🎉' : flash === 'win' ? 'Acertou!' : 'Nenhum…'}
            </div>
          ) : null}
        </div>
      ) : null}

      {phase === 'over' ? (
        <div className="sl-flappy__over">
          <h2>{newRecord ? '🏆 Novo recorde!' : jackpots >= 2 ? 'Sortudo!' : 'Tente de novo.'}</h2>
          <p>
            Jackpots: <strong>{jackpots}</strong><br />
            XP: <strong>{score}</strong>
          </p>
          <p className="sl-flappy__record">
            🏆 Recorde: <strong>{highScore} XP</strong>
            {newRecord ? ' ✨' : null}
          </p>
          <button type="button" className="sl-btn sl-btn--primary sl-btn--lg sl-btn--upper"
            onClick={() => onExit({ score, spins: SPINS_PER_GAME, jackpots, xp: score })}
          >Voltar pro app</button>
        </div>
      ) : null}
    </div>
  );
}
